<script setup lang="ts">
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-vue-next'
import type { Toast, ToastType } from '@/stores/notifications'

interface Props {
  toasts: Toast[]
}

defineProps<Props>()

const emit = defineEmits<{
  clear: [id: string]
}>()

const iconMap: Record<ToastType, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
}

const colorMap: Record<ToastType, string> = {
  success: 'border-l-success-500 bg-white',
  error: 'border-l-danger-500 bg-white',
  warning: 'border-l-warning-500 bg-white',
  info: 'border-l-info-500 bg-white',
}

const iconColorMap: Record<ToastType, string> = {
  success: 'text-success-500',
  error: 'text-danger-500',
  warning: 'text-warning-500',
  info: 'text-info-500',
}
</script>

<template>
  <div
    class="fixed bottom-6 right-6 flex flex-col gap-2"
    style="z-index: var(--z-toast);"
  >
    <TransitionGroup name="toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="['flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg border-l-4 min-w-[300px] max-w-[400px]', colorMap[toast.type]]"
      >
        <component :is="iconMap[toast.type]" :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorMap[toast.type]]" />
        <p class="flex-1 text-sm text-neutral-700">{{ toast.message }}</p>
        <button
          class="text-neutral-400 hover:text-neutral-600 transition-colors flex-shrink-0"
          @click="emit('clear', toast.id)"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 300ms ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
